import React, { useEffect, useState } from 'react';

import stateCodeToName from '@/constants/stateCodeToName';
import type { IntlAddress } from '@/typings/api-models';

import Button from '../../Button';
import s from '../AddressUS/AddressUS.module.scss';
import { Input } from '../Input';
import Select from '../Select';
import Fieldset from '../shared/Fieldset';

import { COUNTRIES, DEFAULT_VALUE } from './constants';

const AddressIntl: React.FC<
  Pick<Parameters<typeof Fieldset>[0], 'label' | 'description' | 'required'> & {
    value: IntlAddress | null;
    onChange: (address: IntlAddress) => void;
    disabled?: boolean;
    onBlur?: () => void;
    onFocus?: () => void;
  } & {
    error?: string;
    hasError?: boolean;
  }
> = ({ label, description, required, value, onChange, onBlur, onFocus, disabled, error, hasError }) => {
  const [address, setAddress] = useState(value || DEFAULT_VALUE);
  const derivedHasError = Boolean(error || hasError);

  const handleInputChange = (field: string) => (v: string) => {
    const newAddress = { ...address, [field]: v };

    setAddress(newAddress);
    onChange(newAddress);
  };

  useEffect(() => {
    setAddress(value ?? DEFAULT_VALUE);
  }, [value]);

  return (
    <Fieldset label={label} description={description} required={required} error={error}>
      <div className={s['address-us']}>
        {address !== DEFAULT_VALUE && (
          <Button variant='secondary' className={s.clear} size='xs' onClick={() => setAddress(DEFAULT_VALUE)}>
            Clear
          </Button>
        )}
        <Input
          label='Street address'
          compact
          value={address.line1}
          onChange={handleInputChange('line1')}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
          hasError={derivedHasError}
        />
        <Input
          label='Apartment / Floor / Suite number'
          compact
          value={address.line2}
          onChange={handleInputChange('line2')}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
          optional
          hasError={derivedHasError}
        />
        {address.country === 'US' && (
          <Select
            label='Country'
            compact
            placeholder='Select'
            options={COUNTRIES}
            value={address.country}
            onChange={handleInputChange('country')}
            disabled={disabled}
            onFocus={onFocus}
            hasError={derivedHasError}
          />
        )}
        <div className={s.row}>
          {address.country !== 'US' && (
            <Select
              label='Country'
              compact
              placeholder='Select'
              options={COUNTRIES}
              value={address.country}
              onChange={handleInputChange('country')}
              disabled={disabled}
              onFocus={onFocus}
              hasError={derivedHasError}
            />
          )}
          <Input
            label='City'
            compact
            value={address.city}
            onChange={handleInputChange('city')}
            disabled={disabled}
            onBlur={onBlur}
            onFocus={onFocus}
            hasError={derivedHasError}
          />
          {address.country === 'US' && (
            <Select
              label='State'
              compact
              placeholder='Select'
              options={stateCodeToName}
              value={address.state}
              onChange={handleInputChange('state')}
              disabled={disabled}
              onFocus={onFocus}
              hasError={derivedHasError}
            />
          )}
          <Input
            label='Post Code'
            compact
            value={address.postCode}
            onChange={handleInputChange('postCode')}
            onBlur={onBlur}
            disabled={disabled}
            onFocus={onFocus}
            hasError={derivedHasError}
          />
        </div>
      </div>
    </Fieldset>
  );
};

export default AddressIntl;
